import { CONFIG, ENV, PERM, TOKEN } from '@components/Environment.js';
import { getDeepProperty, isArray, replaceTokens } from '@components/Utility/index.js';
// currently we are NOT supporting config based conditions on the client side code yet. Just perms.
export class ConditionsManager {
    validateConditions(conditions, context) {
        if (!conditions) {
            return true;
        }
        if (typeof conditions === 'string') {
            try {
                conditions = JSON.parse(conditions);
            }
            catch (e) {
                console.error('Invalid JSON string:', conditions);
                return false; // or handle the error as needed
            }
        }
        if (typeof conditions === 'boolean') {
            return conditions;
        }
        // if any condition passes, this function returns true. 
        return conditions.conditions.some(condition => this.validateCondition(condition, context));
    }
    checkPerm(permname) {
        const permValue = PERM(permname);
        if (permValue) {
            // ignore admin flags are handled because we return N for those when an admin is signed in
            return permValue === 'Y';
        }
        return ENV('userType') === 'ADMIN';
    }
    checkEnv(envCondition) {
        const envValue = String(ENV(envCondition.name) ?? '').toLowerCase();
        const value = envCondition.value.toLowerCase();
        switch (envCondition.operator) {
            case '=':
                return envValue === value;
            case '!=':
                return envValue !== value;
            case 'IN':
                return value.split(',').map(v => v.trim()).indexOf(envValue) > -1;
            case 'NOT IN':
                return value.split(',').map(v => v.trim()).indexOf(envValue) === -1;
            default:
                return false;
        }
    }
    checkData(dataCondition, context) {
        let value = dataCondition.value;
        let token = dataCondition.token;
        if (!context.recordStore) {
            context.recordStore = context.form?.recordStore || context.initiator?.recordStore;
        }
        if (token && token.indexOf('.') > -1) {
            token = replaceTokens(token, context.tokenData, {
                parseTokenCallback: (token, _modifier) => {
                    if (token.indexOf('.') > -1 && context.tokenData) {
                        return getDeepProperty(context.tokenData, token);
                    }
                }
            });
        }
        else if (token) {
            token = TOKEN(token);
        }
        if (value.indexOf('{{') > -1) {
            value = replaceTokens(value, context.tokenData, {
                records: context.recordStore?.recordsGet(),
                parseTokenCallback: (token, _modifier) => {
                    if (token.indexOf('.') > -1 && context.tokenData) {
                        return getDeepProperty(context.tokenData, token);
                    }
                }
            });
        }
        let record = context.record;
        if (dataCondition.recordName && record?.id !== dataCondition.recordName) {
            if (!context.recordStore) {
                console.error('Invalid condition');
                return false;
            }
            record = context.recordStore.recordGet(dataCondition.recordName);
        }
        if (!record && !dataCondition.token) {
            console.error('Invalid condition');
            return false;
        }
        if (!dataCondition.token && !dataCondition.column) {
            console.error('Invalid condition');
            return false;
        }
        const rawColumnValue = dataCondition.token ? (token || '') : (record.get(dataCondition.column) || '');
        if (dataCondition.dataType === 'numeric') {
            const toNum = (v) => {
                const n = parseFloat(v);
                return isNaN(n) ? 0 : n;
            };
            const numCol = toNum(rawColumnValue);
            const numVal = toNum(value);
            switch (dataCondition.operator) {
                case '=':
                    return numCol === numVal;
                case '!=':
                    return numCol !== numVal;
                case '>':
                    return numCol > numVal;
                case '<':
                    return numCol < numVal;
                case '>=':
                    return numCol >= numVal;
                case '<=':
                    return numCol <= numVal;
                case 'IN':
                    return value.split(',').map(v => toNum(v)).indexOf(numCol) > -1;
                case 'NOT IN':
                    return value.split(',').map(v => toNum(v)).indexOf(numCol) === -1;
            }
        }
        const columnValue = rawColumnValue.toLowerCase();
        switch (dataCondition.operator) {
            case '=':
                return columnValue === value.toLowerCase();
            case '!=':
                return columnValue !== value.toLowerCase();
            case 'IN':
                return value.toLowerCase().split(',').indexOf(columnValue) > -1;
            case 'NOT IN':
                return value.toLowerCase().split(',').indexOf(columnValue) === -1;
            case 'STARTSWITH':
                return value.toString().toLowerCase().startsWith(columnValue);
            case 'ENDSWITH':
                return value.toString().toLowerCase().endsWith(columnValue);
            case 'NOT STARTSWITH':
                return !value.toString().toLowerCase().startsWith(columnValue);
            case 'NOT ENDSWITH':
                return !value.toString().toLowerCase().endsWith(columnValue);
            default:
                return false;
        }
    }
    validateCondition(condition, context) {
        // in case someone accidentally leaves an empty condition object in their conditions array, we dont want to consider that a "pass" or it will pass for any user.
        if (!Object.keys(condition).length) {
            return false;
        }
        function checkConfigItems(items) {
            for (const c of items) {
                if (Array.isArray(c)) {
                    // recurse into nested array
                    if (!checkConfigItems(c)) {
                        return false;
                    }
                }
                else {
                    const key = c.key ? parseInt(c.key, 10) : 1;
                    const cfg = CONFIG((c.item || '').toUpperCase());
                    const operator = (c.operator === '' ? '=' : c.operator) || '=';
                    const val = (typeof c.value === 'string' && c.value.startsWith('{{') && c.value.endsWith('}}')) ? TOKEN(c.value) : c.value;
                    if (!cfg || typeof cfg !== 'object' || Object.keys(cfg).length === 0) {
                        console.warn(`The config item ${c.item} is not loaded in the environment and cannot be checked in a client side condition.`);
                        continue; // don't fail - just warn and continue
                    }
                    const config = (CONFIG(c.item, key) ?? '');
                    const normalizedConfig = config.trim().toUpperCase();
                    const list = operator === 'IN' || operator === 'NOT IN'
                        ? val
                            .split(',')
                            .map((v) => v.trim().toUpperCase())
                        : [];
                    if (operator === '=' && normalizedConfig !== val.trim().toUpperCase()) {
                        return false;
                    }
                    if (operator === '!=' && normalizedConfig === val.trim().toUpperCase()) {
                        return false;
                    }
                    if (operator === 'IN' && !list.includes(normalizedConfig)) {
                        return false;
                    }
                    if (operator === 'NOT IN' && list.includes(normalizedConfig)) {
                        return false;
                    }
                }
            }
            return true;
        }
        let conditionpass = true;
        if (condition.config && condition.config.length) {
            conditionpass = checkConfigItems(condition.config);
        }
        if (!conditionpass) {
            return false;
        }
        if (condition.env && condition.env.length) {
            for (const envCondition of condition.env) {
                if (isArray(envCondition)) {
                    if (!envCondition.some(inner => this.checkEnv(inner))) {
                        return false;
                    }
                }
                else {
                    if (!this.checkEnv(envCondition)) {
                        return false;
                    }
                }
            }
        }
        if ((!condition.perms || !condition.perms.length)
            && (!condition.data || !condition.data.length)) {
            return true;
        }
        let pass = true;
        condition.perms?.forEach((perm) => {
            if (typeof perm === 'string') {
                if (!this.checkPerm(perm)) {
                    pass = false;
                }
            }
            else {
                // inner array means any one of the perms in the inner array is a pass (PermA OR PermB)
                if (!perm.some(permname => this.checkPerm(permname))) {
                    pass = false;
                }
            }
        });
        if (pass && condition.data && context) {
            condition.data.forEach((dataCondition) => {
                if (isArray(dataCondition)) {
                    // inner array means any one of the dataConditions in the inner array is a pass (ConditionA OR ConditionB)
                    if (!dataCondition.some(innerDataCondition => this.checkData(innerDataCondition, context))) {
                        pass = false;
                    }
                }
                else {
                    if (!this.checkData(dataCondition, context)) {
                        pass = false;
                    }
                }
            });
        }
        return pass;
    }
}
